"use client";

import { useState } from "react";
import Button, { ButtonVariant } from "@/app/(components)/generics/Button/Button";
import { Input, TextInputType } from "@/app/(components)/generics/inputs/default/Input/Input";
import { SelectInput } from "@/app/(components)/generics/inputs/default/SelectInput/SelectInput";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import { updatePatient } from "@/app/actions/patient/action";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { T, TErrors } from "@/app/translations/Translations";
import { PublicPatient } from "@/app/types/PublicPatient";
import styles from "./EditPatient.module.css";

type Props = { lang: string; patient: PublicPatient; onSuccess?: () => void };

function toDateString(date: Date | null): string {
  if (!date) return "";
  const d = new Date(date);
  return d.toISOString().split("T")[0];
}

export default function EditPatient({ lang, patient, onSuccess }: Props) {
  const { closeModal } = ModalStore();

  const [data, setData] = useState({
    firstName: patient.firstName,
    lastName: patient.lastName,
    birthDate: toDateString(patient.birthDate),
    gender: patient.gender ?? "",
    shoeSize: patient.shoeSize ?? 0,
    footSize: patient.footSize != null ? String(patient.footSize) : "",
    weight: patient.weight != null ? String(patient.weight) : "",
    notes: patient.notes ?? "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});
    setLoading(true);

    const result = await updatePatient(
      patient.uuid,
      {
        firstName: data.firstName,
        lastName: data.lastName,
        birthDate: data.birthDate || undefined,
        gender: data.gender || null,
        shoeSize: data.shoeSize || null,
        footSize: data.footSize || undefined,
        weight: data.weight || undefined,
        notes: data.notes || undefined,
      },
      lang,
    );

    if (result.success) {
      onSuccess?.();
      closeModal();
    } else if (result.fieldErrors) {
      setFieldErrors(TErrors(result.fieldErrors, lang));
    } else {
      setError(T(TKey.ERROR, lang) as string);
    }

    setLoading(false);
  };

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <div className={styles.row}>
        <Input
          data={data}
          dataKey="firstName"
          label={T(TKey.FIRST_NAME, lang) as string}
          lang={lang}
          required
          showRequiredLabel
          placeholder={T(TKey.FIRST_NAME, lang) as string}
          onChange={(d) => setData({ ...d })}
          errorMessage={fieldErrors.firstName}
        />
        <Input
          data={data}
          dataKey="lastName"
          label={T(TKey.LAST_NAME, lang) as string}
          lang={lang}
          required
          showRequiredLabel
          placeholder={T(TKey.LAST_NAME, lang) as string}
          onChange={(d) => setData({ ...d })}
          errorMessage={fieldErrors.lastName}
        />
      </div>

      <div className={styles.row}>
        <Input
          data={data}
          dataKey="birthDate"
          type={TextInputType.DATE}
          label={T(TKey.PATIENT_BIRTH_DATE, lang) as string}
          lang={lang}
          showRequiredLabel
          onChange={(d) => setData({ ...d })}
        />
        <SelectInput
          data={data}
          dataKey="gender"
          label={T(TKey.PATIENT_GENDER, lang) as string}
          lang={lang}
          showRequiredLabel
          withEmptyOption
          options={[
            ["MALE", T(TKey.PATIENT_GENDER_MALE, lang) as string],
            ["FEMALE", T(TKey.PATIENT_GENDER_FEMALE, lang) as string],
            ["OTHER", T(TKey.PATIENT_GENDER_OTHER, lang) as string],
          ]}
          onChange={(d) => setData({ ...d })}
        />
      </div>

      <div className={styles.row}>
        <Input
          data={data}
          dataKey="shoeSize"
          type={TextInputType.NUMBER}
          label={T(TKey.PATIENT_SIZE_EU, lang) as string}
          lang={lang}
          showRequiredLabel
          placeholder=""
          hideZero
          onChange={(d) => setData({ ...d })}
        />
        <Input
          data={data}
          dataKey="footSize"
          label={T(TKey.PATIENT_SIZE_MM, lang) as string}
          lang={lang}
          showRequiredLabel
          placeholder=""
          onChange={(d) => setData({ ...d })}
        />
      </div>

      <Input
        data={data}
        dataKey="weight"
        label={T(TKey.PATIENT_WEIGHT, lang) as string}
        lang={lang}
        showRequiredLabel
        placeholder=""
        onChange={(d) => setData({ ...d })}
      />

      <Input
        data={data}
        dataKey="notes"
        type={TextInputType.TEXTAREA}
        label={T(TKey.PATIENT_NOTES, lang) as string}
        lang={lang}
        showRequiredLabel
        placeholder=""
        onChange={(d) => setData({ ...d })}
      />

      {error && <ErrorMessage message={error} />}

      <div className={styles.actions}>
        <div className={styles.actionsEnd}>
          <Button variant={ButtonVariant.SECONDARY} onClick={closeModal}>
            {T(TKey.CANCEL, lang)}
          </Button>
          <Button variant={ButtonVariant.PRIMARY} type="submit" loading={loading}>
            {T(TKey.SAVE, lang)}
          </Button>
        </div>
      </div>
    </form>
  );
}
